import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getManagerAvailableCyclesAPI, 
  getManagerCycleEmployeesStatusAPI, 
  getManagerAppraisalResponsesAPI,
  createPeerReviewNominationsAPI,
  getPeerReviewNominationsAPI,
  deletePeerReviewNominationAPI,
  getManagerTeamMemberResponsesAPI,
  lockUnlockCycleAPI,
  generateCycleReportAPI,
  getAllEmployeesForReviewsAssignment,
  getEmployeeSelfReviewCyclesAPI
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab";
import { 
  MdAdd, MdCheckCircle, MdPending, MdExpandMore, 
  MdExpandLess, MdVisibility, MdLock, MdLockOpen, 
  MdDownload, MdPictureAsPdf, MdEdit 
} from "react-icons/md";
import { generateAppraisalPDF } from "utils/reportGenerator";
interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface CycleWithEmployees extends AvailableCycle {
  completedEmployees: Employee[];
  pendingEmployees: Employee[];
  locked?: boolean;
  enable_manager_review?: boolean;
  enable_peer_review?: boolean;
  enable_client_review?: boolean;
}
const ManagerAppraisals = () => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cyclesWithEmployees, setCyclesWithEmployees] = useState<CycleWithEmployees[]>([]);
  const [completedResponses, setCompletedResponses] = useState<AppraisalResponse[]>([]);
  const [showNominationModal, setShowNominationModal] = useState(false);
  const [selectedReviewee, setSelectedReviewee] = useState<Employee | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<CycleWithEmployees | null>(null);
  const [selectedReviewers, setSelectedReviewers] = useState<number[]>([]);
  const [managerTeamMembers, setManagerTeamMembers] = useState<Employee[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [existingNominations, setExistingNominations] = useState<any[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [employeeResponses, setEmployeeResponses] = useState<Record<string, AppraisalResponse[]>>({});
  const [loadingResponses, setLoadingResponses] = useState<Record<string, boolean>>({});
  const [expandedCompletedCards, setExpandedCompletedCards] = useState<Set<string>>(new Set());
  const [completedEmployeeResponses, setCompletedEmployeeResponses] = useState<Record<string, AppraisalResponse[]>>({});
  const [loadingCompletedResponses, setLoadingCompletedResponses] = useState<Record<string, boolean>>({});
  const [generatingPdf, setGeneratingPdf] = useState<Record<string, boolean>>({}); // Added state
  

  useEffect(() => {
    fetchAvailableCycles();
    fetchCompletedResponses();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  // Re-added Individual Report Handler
  const handleDownloadIndividualReport = async (e: React.MouseEvent, cycleId: number, employee: Employee, cycleName: string) => {
    e.stopPropagation();
    const key = `${cycleId}-${employee.id}`;
    setGeneratingPdf(prev => ({ ...prev, [key]: true }));

    try {
      let responses = employeeResponses[key] || completedEmployeeResponses[key];
      if (!responses) {
        const apiResponse = await getManagerTeamMemberResponsesAPI(cycleId, employee.id);
        responses = apiResponse.data || [];
      }

      if (responses && responses.length > 0) {
        generateAppraisalPDF(cycleName, employee, responses);
        toast.success("Individual report generated");
      } else {
        toast.warning("No review data found for this employee.");
      }
    } catch (err) {
      toast.error("Failed to generate report.");
    } finally {
      setGeneratingPdf(prev => ({ ...prev, [key]: false }));
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowNominationModal(false);
        setSelectedReviewee(null);
        setSelectedCycle(null);
        setSelectedReviewers([]);
        setExistingNominations([]);
      }
    };

    if (showNominationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [showNominationModal]);

  const [mySelfReviewCycles, setMySelfReviewCycles] = useState<AvailableCycle[]>([]);

  const fetchAvailableCycles = async () => {
    try {
      setLoading(true);
      const res = await getManagerAvailableCyclesAPI();
      const selfRes = await getEmployeeSelfReviewCyclesAPI();
      setMySelfReviewCycles(selfRes.data);
      const cyclesWithStatus = await Promise.all(
        res.data.map(async (cycle: any) => {
          try {
            const statusResponse = await getManagerCycleEmployeesStatusAPI(cycle.id);
            return {
              ...cycle,
              completedEmployees: statusResponse.data.completed || [],
              pendingEmployees: statusResponse.data.pending || [],
              enable_manager_review: cycle.enable_manager_review || false,
              enable_peer_review: cycle.enable_peer_review || false,
              enable_client_review: cycle.enable_client_review || false
            };
          } catch {
            return { 
              ...cycle, 
              completedEmployees: [], 
              pendingEmployees: [],
              enable_manager_review: cycle.enable_manager_review || false,
              enable_peer_review: cycle.enable_peer_review || false,
              enable_client_review: cycle.enable_client_review || false
            };
          }
        })
      );
      setCyclesWithEmployees(cyclesWithStatus);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedResponses = async () => {
    try {
      const response = await getManagerAppraisalResponsesAPI('SUBMITTED');
      setCompletedResponses(response.data);
    } catch (err) {}
  };
  

  const handleOpenNominationModal = async (employee: Employee, cycle: CycleWithEmployees, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedReviewee(employee);
    setSelectedCycle(cycle);
    setSelectedReviewers([]);
    setExistingNominations([]);
    setShowNominationModal(true);
    
    try {
      setLoadingTeamMembers(true);
      const [teamMembersResponse, nominationsResponse] = await Promise.all([
        getAllEmployeesForReviewsAssignment(),
        getPeerReviewNominationsAPI(cycle.id)
      ]);
      
      const teamMembers: Employee[] = teamMembersResponse.map((member: any) => ({
        id: member.id,
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        full_name: member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown',
        photo: member.photo,
        email: member.email,
        phone: member.phone,
        designation: member.designation,
      }));
      setManagerTeamMembers(teamMembers);
      
      const nominations = nominationsResponse.data || [];
      const revieweeNominations = nominations.filter((nom: any) => {
        const revieweeId = typeof nom.reviewee === 'object' ? nom.reviewee?.id : (nom.reviewee_id || nom.reviewee);
        return revieweeId === employee.id;
      });
      setExistingNominations(revieweeNominations);
      
      const existingReviewerIds = revieweeNominations.map((nom: any) => {
        if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
          return nom.reviewer.id;
        }
        return nom.reviewer_id || nom.reviewer;
      }).filter((id: number) => id !== undefined && id !== null);
      setSelectedReviewers(existingReviewerIds);
    } catch (err: any) {
      toast.error("Failed to load data");
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleCloseNominationModal = () => {
    setShowNominationModal(false);
    setSelectedReviewee(null);
    setSelectedCycle(null);
    setSelectedReviewers([]);
    setExistingNominations([]);
  };

  const handleToggleReviewer = (reviewerId: number) => {
    setSelectedReviewers(prev => 
      prev.includes(reviewerId) 
        ? prev.filter(id => id !== reviewerId)
        : [...prev, reviewerId]
    );
  };

  const handleSubmitNominations = async () => {
    if (!selectedReviewee || !selectedCycle) {
      return;
    }

    try {
      setSubmitting(true);
      
      const existingReviewerIds = new Set(
        existingNominations.map((nom: any) => {
          if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
            return nom.reviewer.id;
          }
          return nom.reviewer_id || nom.reviewer;
        }).filter((id: number) => id !== undefined && id !== null)
      );
      const selectedReviewerIds = new Set(selectedReviewers);
      
      const toDelete = existingNominations.filter((nom: any) => {
        let reviewerId;
        if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
          reviewerId = nom.reviewer.id;
        } else {
          reviewerId = nom.reviewer_id || nom.reviewer;
        }
        return reviewerId && !selectedReviewerIds.has(reviewerId);
      });
      
      const toAdd = Array.from(selectedReviewerIds).filter(
        (reviewerId) => !existingReviewerIds.has(reviewerId)
      );
      
      const deletePromises = toDelete.map((nom: any) =>
        deletePeerReviewNominationAPI(selectedCycle.id, nom.id)
      );
      
      await Promise.all(deletePromises);
      
      if (toAdd.length > 0) {
        const newNominations = toAdd.map(reviewerId => ({
          reviewer_id: reviewerId,
          reviewee_id: selectedReviewee.id
        }));
        await createPeerReviewNominationsAPI(selectedCycle.id, newNominations);
      }
      
      const actionMessages = [];
      if (toDelete.length > 0) actionMessages.push(`removed ${toDelete.length}`);
      if (toAdd.length > 0) actionMessages.push(`added ${toAdd.length}`);
      
      if (actionMessages.length > 0) {
        toast.success(`Peer nominations ${actionMessages.join(' and ')} successfully`);
      } else {
        toast.info("No changes made");
      }
      
      handleCloseNominationModal();
      fetchAvailableCycles();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to update peer nominations");
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableReviewers = () => {
  if (!selectedReviewee) return [];
  // Removing the filter allows the manager to select themselves if they are in the list
  return managerTeamMembers; 
};

  const toggleCardExpansion = async (cycleId: number, employeeId: number) => {
    const cardKey = `${cycleId}-${employeeId}`;
    const isExpanded = expandedCards.has(cardKey);
    
    if (isExpanded) {
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardKey);
        return newSet;
      });
    } else {
      setExpandedCards(prev => new Set(prev).add(cardKey));
      
      if (!employeeResponses[cardKey]) {
        try {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: true }));
          const response = await getManagerTeamMemberResponsesAPI(cycleId, employeeId);
          setEmployeeResponses(prev => ({
            ...prev,
            [cardKey]: response.data || []
          }));
        } catch (err: any) {
          toast.error("Failed to load responses");
        } finally {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: false }));
        }
      }
    }
  };

  const toggleCompletedCardExpansion = async (cycleId: number, employeeId: number) => {
    const cardKey = `${cycleId}-${employeeId}`;
    const isExpanded = expandedCompletedCards.has(cardKey);
    
    if (isExpanded) {
      setExpandedCompletedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardKey);
        return newSet;
      });
    } else {
      setExpandedCompletedCards(prev => new Set(prev).add(cardKey));
      
      if (!completedEmployeeResponses[cardKey]) {
        try {
          setLoadingCompletedResponses(prev => ({ ...prev, [cardKey]: true }));
          const response = await getManagerTeamMemberResponsesAPI(cycleId, employeeId);
          setCompletedEmployeeResponses(prev => ({
            ...prev,
            [cardKey]: response.data || []
          }));
        } catch (err: any) {
          toast.error("Failed to load responses");
        } finally {
          setLoadingCompletedResponses(prev => ({ ...prev, [cardKey]: false }));
        }
      }
    }
  };

  const calculateScore = (response: AppraisalResponse): number => {
    if (!response.question_responses || response.question_responses.length === 0) {
      return 0;
    }
    
    let totalScore = 0;
    let hasValidQuestions = false;
    
    response.question_responses.forEach((qr) => {
      const weightage = qr.question_weightage;
      const rating = qr.rating;
      
      if (weightage != null && weightage !== undefined && rating != null && rating !== undefined) {
        const questionScore = (Number(rating) / 5) * Number(weightage);
        totalScore += questionScore;
        hasValidQuestions = true;
      }
    });
    
    if (!hasValidQuestions) return 0;
    return Number(totalScore.toFixed(2));
  };

  const getReviewTypeLabel = (type: string) => {
    switch (type) {
      case 'SELF': return 'Self Review';
      case 'MANAGER': return 'Manager Review';
      case 'PEER': return 'Peer Review';
      case 'CLIENT': return 'Client Review';
      default: return type;
    }
  };

  const getReviewTypeColor = (type: string) => {
    switch (type) {
      case 'SELF': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'MANAGER': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
      case 'PEER': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
      case 'CLIENT': return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };


  const handleDownloadReport = async (cycleId: number, cycleName: string) => {
    try {
      const blob = await generateCycleReportAPI(cycleId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appraisal_report_${cycleName.replace(/\s+/g, '_')}_${cycleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Report downloaded successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to generate report");
    }
  };

  if (loading && cyclesWithEmployees.length === 0) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      <TabsContainer defaultTab={0}>
        <Tab isActive={false} title="Pending Reviews">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Pending Reviews
            </h2>

            {cyclesWithEmployees.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No pending appraisal cycles at this time.
              </div>
            ) : (
              <div className="space-y-4">
                {cyclesWithEmployees.map((cycle) => (
                  <div
                    key={cycle.id}
                    className="border border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                            {cycle.name}
                          </h3>
                          {cycle.locked && (
                            <span className="px-2 py-1 text-xs font-semibold text-orange-600 bg-orange-100 dark:bg-orange-900/20 rounded flex items-center gap-1">
                              <MdLock className="h-3 w-3" />
                              Locked
                            </span>
                          )}
                          {isDeadlinePassed(cycle.deadline) && (
                            <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 dark:bg-red-900/20 rounded">
                              Deadline Passed
                            </span>
                          )}
                        </div>
                        {cycle.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {cycle.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4 hidden">
                        {cycle.locked && (
                          <>
                            <Button
                              varient="primary"
                              onClick={() => navigate(`/manager-appraisals/${cycle.id}/report`)}
                              className="flex items-center gap-2 px-3 py-1 text-sm"
                            >
                              <MdVisibility className="h-4 w-4" />
                              View Report
                            </Button>
                            <Button
                              varient="secondary"
                              onClick={() => handleDownloadReport(cycle.id, cycle.name)}
                              className="flex items-center gap-2 px-3 py-1 text-sm"
                            >
                              <MdDownload className="h-4 w-4" />
                              Download PDF
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Form Opens: </span>
                        <span className="text-navy-700 dark:text-white font-medium">
                          {formatDate(cycle.form_opens)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Deadline: </span>
                        <span className="text-navy-700 dark:text-white font-medium">
                          {formatDate(cycle.deadline)}
                        </span>
                      </div>
                    </div>

                    {cycle.completedEmployees && cycle.completedEmployees.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                          Completed ({cycle.completedEmployees.length}):
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cycle.completedEmployees.map((employee) => (
                            <div
                              key={employee.id}
                              className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow ${
                                isDeadlinePassed(cycle.deadline) ? 'opacity-50' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div
                                  onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/manager-appraisals/${cycle.id}/employee/${employee.id}`)}
                                  className="flex items-center gap-3 flex-1 cursor-pointer"
                                >
                                  {employee.photo ? (
                                    <img
                                      src={employee.photo}
                                      alt={employee.full_name || "Employee"}
                                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                      <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                        {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-navy-700 dark:text-white truncate">
                                      {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                                    </p>
                                    {employee.email && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {employee.email}
                                      </p>
                                    )}
                                    {employee.designation && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                        {employee.designation}
                                      </p>
                                    )}
                                  </div>
                                  <i className="fas fa-check-circle text-gray-500 dark:text-gray-400 text-lg"></i>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div>
                                {cycle.enable_manager_review && (
                                  <Button
                                    varient="secondary"
                                    onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/manager-appraisals/${cycle.id}/employee/${employee.id}`)}
                                    disabled={isDeadlinePassed(cycle.deadline)}
                                    className="w-full flex items-center justify-center gap-2 text-sm mb-2"
                                  >
                                    <MdVisibility className="h-4 w-4" />
                                    View/Edit Review
                                  </Button>
                                )}
                                {cycle.enable_peer_review && (
                                <Button
                                  varient="secondary"
                                  onClick={(e) => handleOpenNominationModal(employee, cycle, e)}
                                  disabled={isDeadlinePassed(cycle.deadline)}
                                  className="w-full flex items-center justify-center gap-2 text-sm mb-2"
                                >
                                  <MdAdd className="h-4 w-4" />
                                  Nominate Peers
                                </Button>
                                )}
                                {/* PDF REPORT BUTTON - Added here */}
                                <Button
                                  varient="secondary"
                                  onClick={(e) => handleDownloadIndividualReport(e, cycle.id, employee, cycle.name)}
                                  disabled={generatingPdf[`${cycle.id}-${employee.id}`]}
                                  className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                                  title="Download Individual Report"
                                >
                                  {generatingPdf[`${cycle.id}-${employee.id}`] ? (
                                    <>
                                      <i className="fas fa-spinner fa-spin text-sm"></i>
                                      <span>Generating...</span>
                                    </>
                                  ) : (
                                    <>
                                      <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                      <span>Download PDF Report</span>
                                    </>
                                  )}
                                </Button>
                                </div>
                                <Button
                                  varient="secondary"
                                  onClick={() => toggleCardExpansion(cycle.id, employee.id)}
                                  className="w-full flex items-center justify-center gap-2 text-sm"
                                >
                                  {expandedCards.has(`${cycle.id}-${employee.id}`) ? (
                                    <>
                                      <MdExpandLess className="h-4 w-4" />
                                      Hide Responses
                                    </>
                                  ) : (
                                    <>
                                      <MdExpandMore className="h-4 w-4" />
                                      View Responses
                                    </>
                                  )}
                                </Button>
                              </div>
                              {expandedCards.has(`${cycle.id}-${employee.id}`) && (
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                                  {loadingResponses[`${cycle.id}-${employee.id}`] ? (
                                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                      <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                                      <p className="text-sm">Loading responses...</p>
                                    </div>
                                  ) : (
                                    <EmployeeResponsesSection
                                      responses={employeeResponses[`${cycle.id}-${employee.id}`] || []}
                                      cycleId={cycle.id}
                                      navigate={navigate}
                                      calculateScore={calculateScore}
                                      getReviewTypeLabel={getReviewTypeLabel}
                                      getReviewTypeColor={getReviewTypeColor}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {cycle.pendingEmployees && cycle.pendingEmployees.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                          Pending ({cycle.pendingEmployees.length}):
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cycle.pendingEmployees.map((employee) => (
                            <div
                              key={employee.id}
                              className={`bg-white dark:bg-navy-800 border-2 border-yellow-200 dark:border-yellow-900/40 rounded-lg p-4 hover:shadow-lg transition-shadow ${
                                isDeadlinePassed(cycle.deadline) ? 'opacity-50' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3 mb-3">
                                <div
                                  onClick={() => {
                                    if (!isDeadlinePassed(cycle.deadline)) {
                                      navigate(`/manager-appraisals/${cycle.id}/employee/${employee.id}`);
                                    }
                                  }}
                                  className={`flex items-center gap-3 flex-1 ${
                                    !isDeadlinePassed(cycle.deadline) 
                                      ? 'cursor-pointer' 
                                      : 'cursor-default'
                                  }`}
                                >
                                  {employee.photo ? (
                                    <img
                                      src={employee.photo}
                                      alt={employee.full_name || "Employee"}
                                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                    <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                        {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-navy-700 dark:text-white truncate">
                                      {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                                    </p>
                                    {employee.email && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {employee.email}
                                      </p>
                                    )}
                                    {employee.designation && (
                                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                        {employee.designation}
                                      </p>
                                    )}
                                  </div>
                                  <i className="fas fa-clock text-brand-500 text-lg"></i>
                                </div>
                              </div>
                              <div className="space-y-2">
                                {cycle.enable_manager_review && (
                                  <Button
                                    varient="primary"
                                    onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/manager-appraisals/${cycle.id}/employee/${employee.id}`)}
                                    disabled={isDeadlinePassed(cycle.deadline)}
                                    className="w-full flex items-center justify-center gap-2 text-sm"
                                  >
                                    <MdEdit className="h-4 w-4" />
                                    Start Review
                                  </Button>
                                )}
                                <Button
                                  varient="secondary"
                                  onClick={(e) => handleDownloadIndividualReport(e, cycle.id, employee, cycle.name)}
                                  disabled={generatingPdf[`${cycle.id}-${employee.id}`]}
                                  className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                                  title="Download Individual Report"
                                >
                                  {generatingPdf[`${cycle.id}-${employee.id}`] ? (
                                    <>
                                      <i className="fas fa-spinner fa-spin text-sm"></i>
                                      <span>Generating...</span>
                                    </>
                                  ) : (
                                    <>
                                      <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                      <span>Download PDF Report</span>
                                    </>
                                  )}
                                </Button>
                                {cycle.enable_peer_review && (
                                  <Button
                                    varient="secondary"
                                    onClick={(e) => handleOpenNominationModal(employee, cycle, e)}
                                    disabled={isDeadlinePassed(cycle.deadline)}
                                    className="w-full flex items-center justify-center gap-2 text-sm"
                                  >
                                    <MdAdd className="h-4 w-4" />
                                    Nominate Peers
                                  </Button>
                                )}
                                <Button
                                  varient="secondary"
                                  onClick={() => toggleCardExpansion(cycle.id, employee.id)}
                                  className="w-full flex items-center justify-center gap-2 text-sm"
                                >
                                  {expandedCards.has(`${cycle.id}-${employee.id}`) ? (
                                    <>
                                      <MdExpandLess className="h-4 w-4" />
                                      Hide Responses
                                    </>
                                  ) : (
                                    <>
                                      <MdExpandMore className="h-4 w-4" />
                                      View Responses
                                    </>
                                  )}
                                </Button>
                              </div>
                              {expandedCards.has(`${cycle.id}-${employee.id}`) && (
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                                  {loadingResponses[`${cycle.id}-${employee.id}`] ? (
                                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                      <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                                      <p className="text-sm">Loading responses...</p>
                                    </div>
                                  ) : (
                                    <EmployeeResponsesSection
                                      responses={employeeResponses[`${cycle.id}-${employee.id}`] || []}
                                      cycleId={cycle.id}
                                      navigate={navigate}
                                      calculateScore={calculateScore}
                                      getReviewTypeLabel={getReviewTypeLabel}
                                      getReviewTypeColor={getReviewTypeColor}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!cycle.completedEmployees || cycle.completedEmployees.length === 0) && (!cycle.pendingEmployees || cycle.pendingEmployees.length === 0) && (
                      <div className="bg-gray-50 dark:bg-gray-900/30 border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          You have no subordinates in this cycle
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                          {(cycle.enable_peer_review || cycle.enable_client_review) && (
                            <>
                              <Button
                                varient="secondary"
                                onClick={(e) => handleDownloadIndividualReport(e, cycle.id, {id: 0, first_name: 'Report', last_name: 'Download', full_name: 'Cycle Report', email: '', designation: ''}, cycle.name)}
                                disabled={generatingPdf[`${cycle.id}-0`]}
                                className="flex items-center justify-center gap-2 px-4 py-2 text-sm"
                                title="Download Cycle Report"
                              >
                                {generatingPdf[`${cycle.id}-0`] ? (
                                  <>
                                    <i className="fas fa-spinner fa-spin text-sm"></i>
                                    <span>Generating...</span>
                                  </>
                                ) : (
                                  <>
                                    <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                    <span>Download PDF</span>
                                  </>
                                )}
                              </Button>
                              {cycle.enable_peer_review && (
                                <Button
                                  varient="secondary"
                                  onClick={() => toast.info('Assign subordinates to this cycle to nominate peers')}
                                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm opacity-50"
                                  disabled={true}
                                >
                                  <MdAdd className="h-4 w-4" />
                                  <span>No Employees</span>
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="My Self Reviews">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">My Personal Appraisals</h2>
            {mySelfReviewCycles.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No self-review cycles active for you.</div>
            ) : (
              <div className="space-y-4">
                {mySelfReviewCycles.map((cycle) => (
                  <div key={cycle.id} className="border-2 border-gray-200 dark:border-navy-700 p-4 rounded-lg flex justify-between items-center bg-white dark:bg-navy-800">
                    <div>
                      <h3 className="font-bold text-navy-700">{cycle.name}</h3>
                      <p className="text-xs text-gray-500">Deadline: {formatDate(cycle.deadline)}</p>
                    </div>
                    <Button 
                      varient="primary" 
                      onClick={() => navigate(`/employee-appraisals/self/${cycle.id}`)}
                      disabled={isDeadlinePassed(cycle.deadline)}
                    >
                      {isDeadlinePassed(cycle.deadline) ? "Deadline Passed" : "Start My Review"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="Completed Reviews">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Completed Reviews
            </h2>

            {completedResponses.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No completed reviews yet.
              </div>
            ) : (
              <div className="space-y-4">
                {completedResponses.map((response) => {
                  const cardKey = `${response.cycle.id}-${response.employee.id}`;
                  const isExpanded = expandedCompletedCards.has(cardKey);
                  
                  return (
                    <div
                      key={response.id}
                      className="bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-5 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
                            {response.cycle.name}
                          </h3>
                          <div className="flex items-center gap-3 mb-3">
                            {response.employee?.photo ? (
                              <img
                                src={response.employee.photo}
                                alt={response.employee?.full_name || "Employee"}
                                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm">
                                  {(response.employee?.first_name?.[0] || '') + (response.employee?.last_name?.[0] || '')}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-navy-700 dark:text-white truncate">
                                {response.employee?.full_name || `${response.employee?.first_name || ''} ${response.employee?.last_name || ''}`.trim() || 'Unknown'}
                              </p>
                              {response.employee?.email && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {response.employee.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-navy-600 rounded-full flex-shrink-0">
                          Submitted
                        </span>
                      </div>

                      <div className="pt-3 border-t border-gray-200 dark:border-navy-700 mb-3">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <i className="fas fa-calendar-check text-gray-500 dark:text-gray-400"></i>
                            <span>
                              {response.submitted_at ? formatDate(response.submitted_at) : (response.created_at ? formatDate(response.created_at) : 'N/A')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              varient="secondary"
                              onClick={() => navigate(`/manager-appraisals/response/${response.id}`)}
                              className="text-xs px-3 py-1"
                            >
                              View Manager Review
                            </Button>
                            <Button
                              varient="secondary"
                              onClick={() => toggleCompletedCardExpansion(response.cycle.id, response.employee.id)}
                              className="text-xs px-3 py-1 flex items-center gap-1"
                            >
                              {isExpanded ? (
                                <>
                                  <MdExpandLess className="h-4 w-4" />
                                  Hide Other Reviews
                                </>
                              ) : (
                                <>
                                  <MdExpandMore className="h-4 w-4" />
                                  View Other Reviews
                                </>
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* PDF REPORT BUTTON - Full width on its own line */}
                        <Button
                          varient="secondary"
                          onClick={(e) => handleDownloadIndividualReport(e, response.cycle.id, response.employee, response.cycle.name)}
                          disabled={generatingPdf[`${response.cycle.id}-${response.employee.id}`]}
                          className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                          title="Download Individual Report"
                        >
                          {generatingPdf[`${response.cycle.id}-${response.employee.id}`] ? (
                            <>
                              <i className="fas fa-spinner fa-spin text-sm"></i>
                              <span>Generating Report...</span>
                            </>
                          ) : (
                            <>
                              <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                              <span>Download PDF Report</span>
                            </>
                          )}
                        </Button>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                          {loadingCompletedResponses[cardKey] ? (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                              <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                              <p className="text-sm">Loading responses...</p>
                            </div>
                          ) : (
                            <EmployeeResponsesSection
                              responses={completedEmployeeResponses[cardKey] || []}
                              cycleId={response.cycle.id}
                              navigate={navigate}
                              calculateScore={calculateScore}
                              getReviewTypeLabel={getReviewTypeLabel}
                              getReviewTypeColor={getReviewTypeColor}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="Reports">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Appraisal Reports
            </h2>

            {cyclesWithEmployees.filter(cycle => cycle.locked).length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No locked cycles available for report generation.
              </div>
            ) : (
              <div className="space-y-4">
                {cyclesWithEmployees
                  .filter(cycle => cycle.locked)
                  .map((cycle) => (
                    <div
                      key={cycle.id}
                      className="border border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                              {cycle.name}
                            </h3>
                            <span className="px-2 py-1 text-xs font-semibold text-orange-600 bg-orange-100 dark:bg-orange-900/20 rounded flex items-center gap-1">
                              <MdLock className="h-3 w-3" />
                              Locked
                            </span>
                          </div>
                          {cycle.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {cycle.description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Form Opens: </span>
                          <span className="text-navy-700 dark:text-white font-medium">
                            {formatDate(cycle.form_opens)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Deadline: </span>
                          <span className="text-navy-700 dark:text-white font-medium">
                            {formatDate(cycle.deadline)}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 hidden">
                        <Button
                          varient="primary"
                          onClick={() => navigate(`/manager-appraisals/${cycle.id}/report`)}
                          className="flex items-center gap-2 px-6 py-2"
                        >
                          <MdVisibility className="h-5 w-5" />
                          View Report
                        </Button>
                        <Button
                          varient="secondary"
                          onClick={() => handleDownloadReport(cycle.id, cycle.name)}
                          className="flex items-center gap-2 px-6 py-2"
                        >
                          <MdDownload className="h-5 w-5" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Card>
        </Tab>
      </TabsContainer>

      {showNominationModal && selectedReviewee && selectedCycle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div 
            ref={modalRef}
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    Nominate Peers for Review
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Select team members who will review <span className="font-semibold">{selectedReviewee.full_name || `${selectedReviewee.first_name} ${selectedReviewee.last_name}`}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Only your direct reports can be selected as reviewers.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Cycle: {selectedCycle.name}
                  </p>
                </div>
                <button
                  onClick={handleCloseNominationModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-gray-600 dark:text-gray-300"></i>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {loadingTeamMembers ? (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                  <p>Loading team members...</p>
                </div>
              ) : getAvailableReviewers().length === 0 ? (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  No available reviewers from your team.
                </div>
              ) : (
                <div className="space-y-3">
                  {getAvailableReviewers().map((reviewer: Employee) => (
                    <label
                      key={reviewer.id}
                      className="flex items-center gap-3 p-4 border border-gray-200 dark:border-navy-700 rounded-lg hover:bg-gray-50 dark:hover:bg-navy-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedReviewers.includes(reviewer.id)}
                        onChange={() => handleToggleReviewer(reviewer.id)}
                        className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                      />
                      <div className="flex items-center gap-3 flex-1">
                        {reviewer.photo ? (
                          <img
                            src={reviewer.photo}
                            alt={reviewer.full_name || "Employee"}
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                            <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm">
                              {(reviewer.first_name?.[0] || '') + (reviewer.last_name?.[0] || '')}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy-700 dark:text-white truncate">
                            {reviewer.full_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() || 'Unknown'}
                          </p>
                          {reviewer.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {reviewer.email}
                            </p>
                          )}
                          {reviewer.designation && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {reviewer.designation}
                            </p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 flex justify-end gap-3">
              <Button
                varient="secondary"
                onClick={handleCloseNominationModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                varient="primary"
                onClick={handleSubmitNominations}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : `Save Changes ${selectedReviewers.length > 0 ? `(${selectedReviewers.length} selected)` : ''}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface EmployeeResponsesSectionProps {
  responses: AppraisalResponse[];
  cycleId: number;
  navigate: (path: string) => void;
  calculateScore: (response: AppraisalResponse) => number;
  getReviewTypeLabel: (type: string) => string;
  getReviewTypeColor: (type: string) => string;
}

const EmployeeResponsesSection: React.FC<EmployeeResponsesSectionProps> = ({
  responses,
  cycleId,
  navigate,
  calculateScore,
  getReviewTypeLabel,
  getReviewTypeColor,
}) => {
  const groupedResponses = {
    MANAGER: responses.find(r => r.reviewer_type === 'MANAGER'),
    SELF: responses.find(r => r.reviewer_type === 'SELF'),
    CLIENT: responses.find(r => r.reviewer_type === 'CLIENT'),
    PEER: responses.filter(r => r.reviewer_type === 'PEER'),
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderResponseCard = (response: AppraisalResponse | undefined, type: string, isMultiple: boolean = false) => {
    if (!response) {
      return (
        <div className="p-3 border border-gray-200 dark:border-navy-700 rounded-lg bg-gray-50 dark:bg-navy-900/30">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
            <MdPending className="h-4 w-4" />
            <span>{getReviewTypeLabel(type)} not submitted yet</span>
          </div>
        </div>
      );
    }

    const score = calculateScore(response);
    const isSubmitted = response.status === 'SUBMITTED';

    return (
      <div className="p-3 border border-gray-200 dark:border-navy-700 rounded-lg bg-white dark:bg-navy-800">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-1 text-xs font-semibold rounded ${getReviewTypeColor(type)}`}>
            {getReviewTypeLabel(type)}
          </span>
          {response.reviewer_name && type === 'PEER' && (
            <span className="text-xs text-gray-600 dark:text-gray-400">
              by {response.reviewer_name}
            </span>
          )}
          {isSubmitted ? (
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <MdCheckCircle className="h-4 w-4" />
              <span className="text-xs font-medium">Submitted</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
              <MdPending className="h-4 w-4" />
              <span className="text-xs font-medium">Pending</span>
            </div>
          )}
        </div>
        {isSubmitted && (
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-navy-700 dark:text-white">
              Score: {score}
            </span>
            <Button
              varient="secondary"
              onClick={() => navigate(`/manager-appraisals/response/${response.id}`)}
              className="text-xs px-3 py-1 flex items-center gap-1"
            >
              <MdVisibility className="h-3 w-3" />
              View
            </Button>
          </div>
        )}
        {response.submitted_at && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Submitted: {formatDate(response.submitted_at)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-navy-700 dark:text-white mb-2">Response Status</h4>
      
      <div className="space-y-2">
        {renderResponseCard(groupedResponses.MANAGER, 'MANAGER')}
        {renderResponseCard(groupedResponses.SELF, 'SELF')}
        {renderResponseCard(groupedResponses.CLIENT, 'CLIENT')}
        
        {groupedResponses.PEER.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Peer Reviews ({groupedResponses.PEER.length}):</p>
            {groupedResponses.PEER.map((peerResponse, idx) => (
              <div key={peerResponse.id || idx}>
                {renderResponseCard(peerResponse, 'PEER', true)}
              </div>
            ))}
          </div>
        ) : (
          renderResponseCard(undefined, 'PEER')
        )}
      </div>
    </div>
  );
};

export default ManagerAppraisals;